import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { Popup } from '../common/Popup';
import { numToPrice } from '../utils.js';
import TeamService from '../../services/teamService';

const CONFIRM_RESET_MS = 4000;

// Pure predicate so the BID intercept is unit-testable in isolation.
// Applies to guests too (every guest has a generated team); the teamId check
// doubles as the team-exists guard and keeps us from intercepting before team
// info has actually loaded, since remainingBudget starts at 0.
function shouldOfferZeroBudgetDrop({ player, teamInfo, isQuickplayLeague }) {
    if (player?.isFreeAgent !== true) return false;
    if (!teamInfo?.teamId || !Number.isFinite(teamInfo?.remainingBudget)) return false;
    if (teamInfo.remainingBudget <= 0) return true;

    // Quick Play bids are fixed at the player's transfer value (see
    // Popups.jsx quickplayFixedBid), so a budget below that fee is just as
    // unbiddable as zero. Squad generation spends almost the whole 600M, so
    // this is the case guests actually hit.
    if (isQuickplayLeague === true) {
        const fee = Number(player?.value ?? player?.marketValue);
        return Number.isFinite(fee) && fee > 0 && teamInfo.remainingBudget < fee;
    }
    return false;
}

function ZeroBudgetDropDialog({
    popOpen,
    close,
    players,
    remainingBudget,
    squadSize,
    teamId,
    leagueId,
    onDropped,
}) {
    const [confirmingWyId, setConfirmingWyId] = useState(null);
    const [droppingWyId, setDroppingWyId] = useState(null);
    const confirmTimeoutRef = useRef(null);

    const sortedPlayers = useMemo(() => (
        [...(players || [])].sort((a, b) => {
            const refundDiff = (b.dropRefund || 0) - (a.dropRefund || 0);
            if (refundDiff !== 0) return refundDiff;
            return (a.name || '').localeCompare(b.name || '');
        })
    ), [players]);

    useEffect(() => () => clearTimeout(confirmTimeoutRef.current), []);

    useEffect(() => {
        if (!popOpen) {
            clearTimeout(confirmTimeoutRef.current);
            setConfirmingWyId(null);
        }
    }, [popOpen]);

    const armConfirm = (wyId) => {
        clearTimeout(confirmTimeoutRef.current);
        setConfirmingWyId(wyId);
        confirmTimeoutRef.current = setTimeout(() => setConfirmingWyId(null), CONFIRM_RESET_MS);
    };

    const handleDrop = async (player) => {
        if (droppingWyId !== null) return;
        clearTimeout(confirmTimeoutRef.current);
        setDroppingWyId(player.wyId);
        try {
            await TeamService.dropPlayer(teamId, player.wyId, leagueId);
            toast.success('Player dropped successfully');
            setConfirmingWyId(null);
            if (onDropped) {
                await onDropped();
            }
        } catch (err) {
            const message = err.response?.data?.message || err.message || 'Failed to drop player';
            toast.error(message);
        } finally {
            setDroppingWyId(null);
        }
    };

    const handleRowButtonClick = (player) => {
        if (confirmingWyId === player.wyId) {
            handleDrop(player);
        } else {
            armConfirm(player.wyId);
        }
    };

    if (!popOpen) return null;

    return (
        <Popup popOpen={popOpen} close={close}>
            <div className="zb-drop-dialog">
                <h3>NOT ENOUGH TRANSFER BUDGET</h3>
                <div className="offer-description text-center">
                    You need to free up funds before you can bid. Drop players from
                    your squad to get their transfer value back.
                </div>
                <div className="zb-drop-dialog__meta">
                    <span>
                        Remaining budget: <strong>{numToPrice(remainingBudget || 0)}</strong>
                    </span>
                    <span className={squadSize <= 22 ? 'zb-drop-dialog__squad-alert' : ''}>
                        Squad size: <strong>{squadSize}</strong>
                    </span>
                </div>
                <ul className="zb-drop-dialog__list">
                    {sortedPlayers.map((player) => (
                        <li key={player.wyId} className="zb-drop-dialog__row">
                            <div className="zb-drop-dialog__player">
                                <span className="zb-drop-dialog__name">{player.name}</span>
                                <span className="zb-drop-dialog__refund">
                                    {numToPrice(player.dropRefund || 0)} back
                                </span>
                            </div>
                            <button
                                type="button"
                                className={`submit-btn zb-drop-dialog__drop-btn ${
                                    confirmingWyId === player.wyId ? 'zb-drop-dialog__drop-btn--confirm' : ''
                                }`}
                                disabled={droppingWyId !== null}
                                onClick={() => handleRowButtonClick(player)}
                            >
                                {droppingWyId === player.wyId
                                    ? 'DROPPING...'
                                    : confirmingWyId === player.wyId
                                        ? 'CONFIRM'
                                        : 'DROP'}
                            </button>
                        </li>
                    ))}
                </ul>
                <div className="buttons-block">
                    <button type="button" className="submit-btn trans-btn white" onClick={close}>
                        BACK TO MARKET
                    </button>
                </div>
            </div>
        </Popup>
    );
}

export { ZeroBudgetDropDialog, shouldOfferZeroBudgetDrop };
